import {
  Box,
  Button,
  Container,
  Divider,
  FormControl,
  Grid,
  InputAdornment,
  OutlinedInput,
  Paper,
  Slider,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLeadGeneration } from "../../hooks/useLeadGeneration";

const EmiCalculator = ({redirectToApplyLoanTab, setShowTalkToAdviser, setShowThankYouPopUp}) => {
  const [loanAmount, setLoanAmount] = useState(10000);
  const [interestRate, setInterestRate] = useState(8.5); // Annual interest rate in percentage
  const [loanTenure, setLoanTenure] = useState(5); // Loan tenure in years
  const [emiAmount, setEmiAmount] = useState(0);
  const [totalInterestAmount, setTotalInterestAmount] = useState(0);
  const [totalRepaymentAmount, setTotalRepaymentAmount] = useState(0);
  const handleLeadGeneration = useLeadGeneration(setShowTalkToAdviser, setShowThankYouPopUp);

  // Calculating EMI and related amounts whenever inputs change
  useEffect(() => {
    const principal = loanAmount;
    const rate = interestRate / 100 / 12;
    const tenure = loanTenure * 12;
    setTimeout(() => {
      if (principal && rate && tenure) {
        const emi =
          (principal * rate * Math.pow(1 + rate, tenure)) /
          (Math.pow(1 + rate, tenure) - 1);
        setEmiAmount(emi?.toFixed(2));
        setTotalInterestAmount((emi * tenure - principal).toFixed(2));
        setTotalRepaymentAmount((emi * tenure).toFixed(2));
      }
    }, 200);
  }, [loanAmount, interestRate, loanTenure]);
  const numberFormatter = (amount) => {
    const loanAmount = Number(amount);
    return loanAmount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const handleNeedCustomizedSolution = () => {
    handleLeadGeneration();
  };

  return (
    <Container maxWidth="lg" style={{ padding: "16px", marginTop: "1rem" }}>
      <Grid container spacing={3} style={{ marginTop: "4px" }}>
        <Grid item xs={12} md={7}>
          <Paper style={{ padding: "24px", backgroundColor: "#F8F8F8" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography
                variant="subtitle1"
                gutterBottom
                style={{ marginTop: "24px" }}
              >
                Loan Amount (₹){" "}
                <Typography component="span" sx={{ color: "red" }}>
                  *
                </Typography>
              </Typography>
              <FormControl
                sx={{ m: 1, width: "21ch", height: "50px" }}
                variant="outlined"
              >
                <OutlinedInput
                  id="outlined-adornment-weight"
                  inputProps={{
                    maxLength: 12, // just extra safety for browser
                  }}
                  value={
                    loanAmount === 0
                      ? ""
                      : new Intl.NumberFormat("en-IN").format(loanAmount)
                  }
                  onChange={(e) => {
                    let value = e.target.value.replace(/,/g, "");
                    const newValue = Number(value);
                    if (!isNaN(newValue)) {
                      setLoanAmount(newValue); // update value without commas
                    }
                  }}
                  placeholder="E.g., 5,00,000"
                  startAdornment={
                    <InputAdornment position="start">
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Box sx={{ pr: 1, fontWeight: 600 }}>INR</Box>
                        <Divider
                          orientation="vertical"
                          flexItem
                          sx={{
                            height: "40px",
                            bgcolor: "5A5A5A",
                            color: "#5A5A5A",
                          }}
                        />
                      </Box>
                    </InputAdornment>
                  }
                />
              </FormControl>
            </Box>
            {/* Slider for loan amount (optional, if you'd like to allow users to adjust the value) */}
            <Box display="flex" alignItems="center">
              <Slider
                value={loanAmount}
                min={10000}
                max={100000000}
                step={10000}
                onChange={(e, newValue) => setLoanAmount(newValue)}
                valueLabelDisplay="none"
                sx={{
                  marginRight: "16px",
                  color: "#BDBDBD",
                  "& .MuiSlider-thumb": {
                    backgroundColor: "#18CB96",
                  },
                  "& .MuiSlider-track": {
                    backgroundColor: "#18CB96",
                  },
                  "& .MuiSlider-rail": {
                    backgroundColor: "#E8E8E8",
                  },
                  "&:active .MuiSlider-track": {
                    backgroundColor: "#18CB96",
                  },
                  height: "8px",
                }}
              />
            </Box>

            {/* Interest Rate input */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: "1rem",
              }}
            >
              <Typography
                variant="subtitle1"
                gutterBottom
                style={{ marginTop: "24px" }}
              >
                Interest Rate (% per annum)*{" "}
                <Typography component="span" sx={{ color: "red" }}>
                  *
                </Typography>
              </Typography>
              <FormControl
                sx={{ m: 1, width: "21ch", height: "50px" }}
                variant="outlined"
              >
                <OutlinedInput
                  id="outlined-adornment-weight"
                  value={interestRate}
                  // disabled
                  onChange={(e) => {
                    let value = e.target.value.replace(/,/g, "");
                    const newValue = Number(value);
                    if (!isNaN(newValue)&&newValue<=20) {
                      setInterestRate(newValue); // update value without commas
                    }
                  }}
                  inputProps={{
                    maxLength: 2, // just extra safety for browser
                  }}
                  placeholder="E.g., 12%"
                  startAdornment={
                    <InputAdornment position="start">
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Box sx={{ pr: 1, fontWeight: 600 }}>%</Box>
                        <Divider
                          orientation="vertical"
                          flexItem
                          sx={{
                            height: "40px",
                            bgcolor: "5A5A5A",
                            color: "#5A5A5A",
                          }}
                        />
                      </Box>
                    </InputAdornment>
                  }
                />
              </FormControl>
            </Box>
            <Box display="flex" alignItems="center">
              <Slider
                value={interestRate}
                min={5}
                max={15}
                step={0.5}
                onChange={(e, newValue) => setInterestRate(newValue)}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `${value}%`}
                sx={{
                  marginRight: "16px",
                  color: "#BDBDBD",
                  "& .MuiSlider-thumb": {
                    backgroundColor: "#18CB96",
                  },
                  "& .MuiSlider-track": {
                    backgroundColor: "#18CB96",
                  },
                  "& .MuiSlider-rail": {
                    backgroundColor: "#E8E8E8",
                  },
                  "&:active .MuiSlider-track": {
                    backgroundColor: "#18CB96",
                  },
                  height: "8px",
                }}
              />
            </Box>

            {/* Loan Tenure input */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: "1rem",
              }}
            >
              <Typography
                variant="subtitle1"
                gutterBottom
                style={{ marginTop: "24px" }}
              >
                Loan Tenure (Years){" "}
                <Typography component="span" sx={{ color: "red" }}>
                  *
                </Typography>
              </Typography>
              <FormControl
                sx={{ m: 1, width: "21ch", height: "50px" }}
                variant="outlined"
              >
                <OutlinedInput
                  id="outlined-adornment-weight"
                  // disabled
                  value={loanTenure}
                  onChange={(e) => {
                    let value = e.target.value.replace(/,/g, "");
                    const newValue = Number(value);
                    if (!isNaN(newValue)&&newValue<=30) {
                      setLoanTenure(newValue); // update value without commas
                    }
                  }}
                  inputProps={{
                    maxLength: 2, // just extra safety for browser
                  }}
                  placeholder="E.g., 3 year"
                  startAdornment={
                    <InputAdornment position="start">
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Box sx={{ pr: 1, fontWeight: 600 }}>Yrs.</Box>
                        <Divider
                          orientation="vertical"
                          flexItem
                          sx={{
                            height: "40px",
                            bgcolor: "5A5A5A",
                            color: "#5A5A5A",
                          }}
                        />
                      </Box>
                    </InputAdornment>
                  }
                />
              </FormControl>
            </Box>
            <Box
              display="flex"
              alignItems="center"
              sx={{ marginBottom: "6.1rem" }}
            >
              <Slider
                value={loanTenure}
                min={1}
                max={30}
                step={1}
                onChange={(e, newValue) => setLoanTenure(newValue)}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `${value} yrs`}
                sx={{
                  marginRight: "16px",
                  color: "#BDBDBD",
                  "& .MuiSlider-thumb": {
                    backgroundColor: "#18CB96",
                  },
                  "& .MuiSlider-track": {
                    backgroundColor: "#18CB96",
                  },
                  "& .MuiSlider-rail": {
                    backgroundColor: "#E8E8E8",
                  },
                  "&:active .MuiSlider-track": {
                    backgroundColor: "#18CB96",
                  },
                  height: "8px",
                }}
              />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Box display="flex" flexDirection="column" gap={2}>
            <Paper style={{ padding: "24px", backgroundColor: "#EDF0FF" }}>
              <Typography
                variant="subtitle1"
                color="textSecondary"
                gutterBottom
                sx={{ color: "#333333", fontWeight: 600 }}
              >
                Your Estimated EMI
              </Typography>
              <Typography
                variant="h5"
                align="left"
                color="orange"
                style={{ fontWeight: 900 }}
              >
                ₹ {numberFormatter(emiAmount)}
              </Typography>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{
                  color: "#5A5A5A",
                  fontStyle: "italic",
                  //   marginBottom: "5px",
                  fontSize: "0.8rem",
                  marginTop: "5px",
                }}
              >
                Based on your loan details, here’s what you’ll need to pay each
                month.
              </Typography>
            </Paper>

            {/* Loan Estimate */}
            <Paper style={{ padding: "24px", backgroundColor: "#EDF0FF" }}>
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{ color: "#333333", fontWeight: 600 }}
              >
                Your Loan Estimate is Ready!
              </Typography>
              <Typography
                sx={{
                  color: "#5A5A5A",
                  fontStyle: "italic",
                  marginBottom: "20px",
                  fontSize: "0.8rem",
                }}
              >
                Here’s a breakdown of your estimated loan repayment details.
                Review these figures to plan your finances confidently.
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <Box display="flex" justifyContent={"space-between"}>
                  <Typography sx={{ color: "#5A5A5A", fontSize: "0.9rem" }}>
                    Total Interest Payable
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.9rem",
                      fontWeight: 600,
                    }}
                  >
                    ₹ {numberFormatter(totalInterestAmount.toLocaleString())}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent={"space-between"}>
                  <Typography sx={{ color: "#5A5A5A", fontSize: "0.9rem" }}>
                    Total Repayment Amount
                  </Typography>
                  <Typography
                    sx={{
                      //   color: "#0A8F4F",
                      fontSize: "0.9rem",
                      fontWeight: 600,
                    }}
                  >
                    ₹ {numberFormatter(totalRepaymentAmount.toLocaleString())}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Button
                    variant="contained"
                    color="#004AAD"
                    style={{
                      marginTop: "5px",
                      backgroundColor: "#004AAD",
                      color: "white",
                      textTransform: "none",
                      width: "70%",
                      height: "50px",
                    }}
                    onClick={()=>redirectToApplyLoanTab()}
                  >
                    Apply Loan Now
                  </Button>
                </Box>
                <Typography
                  variant="body2"
                  align="center"
                  style={{ marginTop: "10px", color: "#004AAD", cursor: "pointer" }}
                  onClick={handleNeedCustomizedSolution}
                >
                  Need Customized Solution?
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default EmiCalculator;
